import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle,
} from '../../../../components/ui/Dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/Table';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import { Spinner } from '../../../../components/ui/Spinner';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { Switch } from '../../../../components/ui/Switch';
import AppIcon, { type IconName } from '../../../../components/AppIcon';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import { useDialogError, DialogErrorAlert } from '../../../../components/ui/DialogError';
import type { ReflectionPrompt, ReflectionPromptInput } from '../../../../models/preformanceManagement';
import {
  getReflectionPromptIcons,
  getReflectionPromptsForManage,
  createReflectionPrompt,
  updateReflectionPrompt,
  setReflectionPromptActive,
  archiveReflectionPrompt,
  reorderReflectionPrompts,
} from '../../../../api/PreformanceManagement/Reflection';
import { getCompanySummaries, type CompanySummary } from '../../../../api/Navigation/moduleConfig';
import { comparePromptOrder, nextDisplayOrder, resolvePromptText } from '../../../PerformanceManagement/Reflections/reflectionPromptText';
import ReflectionPromptEditorDialog from './ReflectionPromptEditorDialog';

/** The first refusal message on a rejected Result envelope, when the rejection is one. */
const refusalOf = (err: unknown): string | undefined =>
  (err as { errors?: { description?: string }[] } | null | undefined)?.errors?.[0]?.description;

/** The picker's value for the platform-defaults scope; company scopes are the company id as a string. */
const PLATFORM = 'platform';

/**
 * Operator-only authoring of reflection prompts in two scopes (owner decision 2026-09-18): the
 * platform defaults every company inherits, and one company's own rows, chosen from the same
 * searchable company picker Module Config uses. HR never reaches this page; every write lands in
 * the security audit log.
 */
const ReflectionPrompts: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();
  // CO-49: a refused write is explained inside the dialog that refused it.
  const dialogError = useDialogError();
  const deleteDialogError = useDialogError();

  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [scope, setScope] = useState<string>(PLATFORM);
  const [prompts, setPrompts] = useState<ReflectionPrompt[]>([]);
  const [icons, setIcons] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ReflectionPrompt | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReflectionPrompt | null>(null);

  /** null = platform defaults; otherwise the selected company. */
  const scopeCompanyId: number | null = scope === PLATFORM ? null : Number(scope);
  const inCompanyScope = scopeCompanyId != null;

  useEffect(() => {
    getCompanySummaries().then((r) => setCompanies(r.companies));
    getReflectionPromptIcons().then((r) => setIcons(r.payload ?? [])).catch(() => setIcons([]));
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getReflectionPromptsForManage(scopeCompanyId);
      if (list.errors?.length) throw list;
      setPrompts([...(list.payload ?? [])].sort(comparePromptOrder));
    } catch (error: unknown) {
      showToast(refusalOf(error) || t('performance.reflectionPrompts.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [scopeCompanyId, showToast, t]);

  useEffect(() => { loadData(); }, [loadData]);

  /** In a company scope a platform default is read-only: it is shared, so it is Customized, not edited. */
  const isReadOnly = (p: ReflectionPrompt) => inCompanyScope && p.companyId == null;

  const openAdd = () => { setEditing(null); dialogError.clear(); setEditorOpen(true); };
  const openEdit = (p: ReflectionPrompt) => { setEditing(p); dialogError.clear(); setEditorOpen(true); };
  const closeEditor = () => { setEditorOpen(false); setEditing(null); dialogError.clear(); };

  const handleSave = async (input: ReflectionPromptInput) => {
    setSaving(true);
    dialogError.clear();
    try {
      // Customizing a platform default inside a company scope is a CREATE that carries the
      // default's key, so the new company row shadows it. Everything else edits the row itself.
      const result = editing && !isReadOnly(editing)
        ? await updateReflectionPrompt(editing.id, input)
        : await createReflectionPrompt(scopeCompanyId, input);
      if (result.errors?.length) throw result;
      showToast(t('performance.reflectionPrompts.saved'), 'success');
      closeEditor();
      await loadData();
    } catch (error: unknown) {
      const parsed = dialogError.capture(error, t('performance.reflectionPrompts.saveError'));
      showToast(parsed.messages[0], 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (p: ReflectionPrompt, isActive: boolean) => {
    try {
      const result = await setReflectionPromptActive(p.id, isActive);
      if (result.errors?.length) throw result;
      await loadData();
    } catch (error: unknown) {
      showToast(refusalOf(error) || t('performance.reflectionPrompts.saveError'), 'error');
    }
  };

  /** Both rows must be writable in this scope: a shared default keeps its number here. */
  const canMove = (index: number, delta: -1 | 1): boolean => {
    const a = prompts[index];
    const b = prompts[index + delta];
    return !!a && !!b && !isReadOnly(a) && !isReadOnly(b);
  };

  const moveTitle = (index: number, delta: -1 | 1, action: string): string =>
    prompts[index + delta] && !canMove(index, delta) ? String(t('performance.reflectionPrompts.moveNeedsCustomize')) : action;

  /** One request swaps the two numbers; the server applies both in a single save, so no row ever
   *  sits on a taken number between two calls. Legacy rows that share a number are separated by
   *  giving the row that ends up second the next number. */
  const handleMove = async (index: number, delta: -1 | 1) => {
    if (!canMove(index, delta)) return;
    const a = prompts[index];
    const b = prompts[index + delta];
    const [first, second] = delta === -1 ? [a, b] : [b, a];
    const lo = Math.min(a.displayOrder, b.displayOrder);
    const hi = a.displayOrder === b.displayOrder ? lo + 1 : Math.max(a.displayOrder, b.displayOrder);
    try {
      const result = await reorderReflectionPrompts([
        { id: first.id, displayOrder: lo },
        { id: second.id, displayOrder: hi },
      ]);
      if (result.errors?.length) throw result;
      await loadData();
    } catch (error: unknown) {
      showToast(refusalOf(error) || t('performance.reflectionPrompts.saveError'), 'error');
    }
  };

  /** Display numbers other visible prompts hold, keyed by number, for the editor's inline check.
   *  The row being edited keeps its own number; a Customize inherits the default it replaces. */
  const takenOrders = useMemo(() => {
    const map: Record<number, string> = {};
    for (const p of prompts) {
      if (editing && p.id === editing.id) continue;
      map[p.displayOrder] = resolvePromptText(p, i18n.language).title;
    }
    return map;
  }, [prompts, editing, i18n.language]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    deleteDialogError.clear();
    try {
      const result = await archiveReflectionPrompt(deleteTarget.id);
      if (result.errors?.length) throw result;
      showToast(t('performance.reflectionPrompts.deleted'), 'success');
      setDeleteTarget(null);
      await loadData();
    } catch (error: unknown) {
      const parsed = deleteDialogError.capture(error, t('performance.reflectionPrompts.saveError'));
      showToast(parsed.messages[0], 'error');
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(() => prompts.map((p) => ({ raw: p, text: resolvePromptText(p, i18n.language) })), [prompts, i18n.language]);

  const scopeOptions = useMemo(() => [
    { value: PLATFORM, label: t('performance.reflectionPrompts.scopePlatform') },
    ...companies.map((c) => ({ value: String(c.companyId), label: c.companyName })),
  ], [companies, t]);

  return (
    <PageLayout
      title={t('performance.reflectionPrompts.pageTitle')}
      description={t('performance.reflectionPrompts.pageSubtitle')}
      icon={BookOpen}
      accentColor="rose"
      headerActions={(
        <Button variant="outline" iconName="PlusCircle" onClick={openAdd} data-testid="rp-add">
          {t('performance.reflectionPrompts.add')}
        </Button>
      )}
    >
      <div className="flex flex-col flex-1 gap-5">
        <div className="rounded-lg border border-border bg-card p-4 sm:max-w-md" data-testid="rp-scope">
          <Select
            label={t('performance.reflectionPrompts.scopeLabel')}
            options={scopeOptions}
            value={scope}
            searchable
            onChange={(v: string) => setScope(v || PLATFORM)}
            data-testid="rp-scope-select"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            {inCompanyScope ? t('performance.reflectionPrompts.scopeCompanyHint') : t('performance.reflectionPrompts.scopePlatformHint')}
          </p>
        </div>

        {loading && !editorOpen ? (
          <div className="flex justify-center py-8"><Spinner size="lg" /></div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-6 text-center">
            <p className="text-muted-foreground">{t('performance.reflectionPrompts.empty')}</p>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12" />
                  <TableHead className="font-semibold">{t('performance.reflectionPrompts.field.title')}</TableHead>
                  <TableHead className="font-semibold">{t('performance.reflectionPrompts.field.category')}</TableHead>
                  <TableHead className="font-semibold">{t('performance.reflectionPrompts.field.active')}</TableHead>
                  <TableHead className="font-semibold text-end">{t('common:Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ raw, text }, index) => {
                  const readOnly = isReadOnly(raw);
                  return (
                    <TableRow key={raw.id} data-testid={`rp-row-${raw.id}`}>
                      <TableCell>
                        <div className="w-9 h-9 rounded-lg bg-muted border border-border flex items-center justify-center">
                          <AppIcon name={(text.iconName as IconName) || 'Award'} size={18} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium" data-testid="rp-title">{text.title}</span>
                          {readOnly && (
                            <StatusBadge variant="neutral" title={t('performance.reflectionPrompts.platformDefaultHint')}>
                              {t('performance.reflectionPrompts.platformDefault')}
                            </StatusBadge>
                          )}
                          {!raw.isActive && <StatusBadge variant="warning">{t('performance.reflectionPrompts.off')}</StatusBadge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 tabular-nums">
                          {t('performance.reflectionPrompts.answers', { count: raw.answerCount })}
                        </p>
                      </TableCell>
                      <TableCell>{text.category}</TableCell>
                      <TableCell>
                        {readOnly ? (
                          <span className="text-xs text-muted-foreground">{t('performance.reflectionPrompts.on')}</span>
                        ) : (
                          <Switch
                            checked={raw.isActive}
                            onCheckedChange={(c) => handleToggle(raw, c)}
                            aria-label={t('performance.reflectionPrompts.field.active')}
                            data-testid={`rp-toggle-${raw.id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" disabled={!canMove(index, -1)} onClick={() => handleMove(index, -1)} aria-label={String(t('performance.reflectionPrompts.moveUp'))} title={moveTitle(index, -1, String(t('performance.reflectionPrompts.moveUp')))} data-testid={`rp-up-${raw.id}`}>
                            <AppIcon name="ChevronUp" size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" disabled={!canMove(index, 1)} onClick={() => handleMove(index, 1)} aria-label={String(t('performance.reflectionPrompts.moveDown'))} title={moveTitle(index, 1, String(t('performance.reflectionPrompts.moveDown')))} data-testid={`rp-down-${raw.id}`}>
                            <AppIcon name="ChevronDown" size={16} />
                          </Button>
                          {readOnly ? (
                            <Button variant="outline" size="sm" onClick={() => openEdit(raw)} title={String(t('performance.reflectionPrompts.platformDefaultHint'))} data-testid={`rp-customize-${raw.id}`}>
                              {t('performance.reflectionPrompts.customize')}
                            </Button>
                          ) : (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => openEdit(raw)} aria-label={String(t('common:Edit'))} title={String(t('common:Edit'))} data-testid={`rp-edit-${raw.id}`}>
                                <AppIcon name="Pencil" size={16} />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => { deleteDialogError.clear(); setDeleteTarget(raw); }} aria-label={String(t('common:Delete'))} title={String(t('common:Delete'))} data-testid={`rp-delete-${raw.id}`}>
                                <AppIcon name="Trash2" size={16} className="text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <ReflectionPromptEditorDialog
          open={editorOpen}
          prompt={editing}
          customize={!!editing && isReadOnly(editing)}
          defaultOrder={nextDisplayOrder(prompts)}
          takenOrders={takenOrders}
          icons={icons}
          saving={saving}
          dialogError={dialogError}
          onSave={handleSave}
          onClose={closeEditor}
        />

        <Dialog open={deleteTarget != null} onOpenChange={(o) => { if (!o && !saving) setDeleteTarget(null); }}>
          <DialogContent size="sm">
            <DialogHeader><DialogTitle>{t('performance.reflectionPrompts.delete')}</DialogTitle></DialogHeader>
            <DialogBody className="space-y-4">
              <DialogErrorAlert error={deleteDialogError.error} onDismiss={deleteDialogError.clear} className="mb-0" />
              <p className="text-sm text-foreground">
                {t('performance.reflectionPrompts.deleteConfirm', { title: deleteTarget ? resolvePromptText(deleteTarget, i18n.language).title : '' })}
              </p>
            </DialogBody>
            <DialogFooter className="justify-center">
              <Button variant="destructive" onClick={handleDelete} disabled={saving} loading={saving} data-testid="rp-delete-confirm">
                {t('common:Delete')}
              </Button>
              <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={saving}>
                {t('common:Cancel')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
};

export default ReflectionPrompts;
